import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import { useClientes } from '../../shared/api/queries.ts';
import type { FiltroDaSituacaoMensal } from '../../shared/api/tipos.ts';
import {
  competenciaAnterior,
  periodoDaCompetencia,
} from '../../shared/ui/SeletorDeCompetencia.tsx';
import { HistoricoDeEnvios } from './HistoricoDeEnvios.tsx';
import { QuebrasDeSequencia } from './QuebrasDeSequencia.tsx';
import { SituacaoDoMes } from './SituacaoDoMes.tsx';
import { XmlsPendentes } from './XmlsPendentes.tsx';

type Aba = 'clientes' | 'xmls' | 'sequencia' | 'historico';

/**
 * Como está o mês, em quatro abas.
 *
 * O eixo é a competência: a primeira aba responde "como está cada loja neste
 * mês" — o que já saiu e o que espera alguém —, a segunda e a terceira abrem
 * uma loja (as notas que não viraram documento fiscal e os números que não
 * chegaram), e a quarta mostra o que o sistema já mandou do mesmo mês. Clicar
 * numa linha da primeira abre a segunda já com o cliente escolhido, em vez de
 * obrigar a reencontrá-lo num seletor.
 *
 * Competência e cliente são estado desta tela, e não de cada aba: trocar de
 * aba continua falando do mesmo mês e da mesma loja. Trocar o mês remonta as
 * abas de período (a `key`), que é o que faz o filtro de datas nascer no mês
 * novo em vez de continuar mostrando o anterior.
 */
export function Pendencias() {
  const [aba, setAba] = useState<Aba>('clientes');
  const [empresaId, setEmpresaId] = useState<string | null>(null);
  // Abre no mês que se está fechando: em outubro, o trabalho é setembro.
  const [competencia, setCompetencia] = useState(competenciaAnterior);
  const [situacao, setSituacao] = useState<'todas' | FiltroDaSituacaoMensal>('todas');

  const clientes = useClientes();
  const itens = clientes.data?.itens ?? [];
  const periodo = periodoDaCompetencia(competencia);

  return (
    <Stack spacing={2}>
      <Card>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            <Typography variant="h2">Pendências</Typography>
            <Typography variant="body2" color="text.secondary">
              Como está cada loja no mês: o que já foi entregue à contabilidade, as notas que
              esperam alguém e os números que não chegaram.
            </Typography>
          </Box>
        </Toolbar>

        <Divider />

        <Tabs
          value={aba}
          onChange={(_, escolhida: Aba) => setAba(escolhida)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="clientes" label="Clientes" />
          <Tab value="xmls" label="XMLs pendentes" />
          <Tab value="sequencia" label="Quebras de sequência" />
          <Tab value="historico" label="Histórico de envios" />
        </Tabs>
      </Card>

      {aba === 'clientes' && (
        <SituacaoDoMes
          competencia={competencia}
          aoMudarCompetencia={setCompetencia}
          situacao={situacao}
          aoMudarSituacao={setSituacao}
          aoEscolherCliente={(escolhido) => {
            setEmpresaId(escolhido);
            setAba('xmls');
          }}
        />
      )}

      {aba === 'xmls' && (
        <XmlsPendentes
          key={competencia}
          clientes={itens}
          carregandoClientes={clientes.isPending}
          empresaId={empresaId}
          aoEscolherCliente={setEmpresaId}
          periodoSugerido={periodo}
        />
      )}

      {aba === 'sequencia' && (
        <QuebrasDeSequencia
          key={competencia}
          clientes={itens}
          carregandoClientes={clientes.isPending}
          empresaId={empresaId}
          aoEscolherCliente={setEmpresaId}
          periodoSugerido={periodo}
        />
      )}

      {aba === 'historico' && (
        <HistoricoDeEnvios
          competencia={competencia}
          clientes={itens}
          carregandoClientes={clientes.isPending}
          empresaId={empresaId}
          aoEscolherCliente={setEmpresaId}
        />
      )}
    </Stack>
  );
}
